// Single node row in the problem/solution space tree
// Supports drag-to-link: draggable nodes + drop targets

import { ChevronRight, Link } from 'lucide-react'
import type { SpaceNode as SpaceNodeType, ConfidenceLevel } from '@ispot/shared'
import type { DragSource } from '@/hooks/useDragToLink'

interface SpaceNodeProps {
  node: SpaceNodeType
  depth: number
  space: 'problem' | 'solution'
  isSelected: boolean
  isHighlighted: boolean
  isDimmed: boolean
  isDropTarget: boolean
  expandedIds: Set<string>
  onSelect: (id: string, space: 'problem' | 'solution') => void
  onToggleExpand: (id: string) => void
  // Drag-to-link
  canDrag: (type: string) => boolean
  isValidDropTarget: (type: string, space: 'problem' | 'solution') => boolean
  onDragStart: (source: DragSource, e: React.DragEvent) => void
  onDragOver: (targetId: string, targetType: string, targetSpace: 'problem' | 'solution', e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (targetType: string, targetId: string, e: React.DragEvent) => void
  onDragEnd: () => void
}

const BADGE_LABELS: Record<string, string> = {
  persona: 'PERSONA', alternative: 'ALT', jtbd: 'JTBD',
  pain: 'PAIN', gain: 'GAIN', need: 'NEED',
  capability: 'CAPABILITY', feature: 'FEATURE', benefit: 'BENEFIT',
}

const BADGE_CLASSES: Record<string, string> = {
  persona: 'bg-warm-200 text-warm-700',
  alternative: 'bg-warm-200 text-warm-600',
  jtbd: 'bg-business/10 text-business',
  pain: 'bg-danger/10 text-danger',
  gain: 'bg-market/10 text-market',
  need: 'bg-solution/12 text-solution-dark',
  capability: 'bg-core/10 text-core',
  feature: 'bg-market/10 text-market',
  benefit: 'bg-business/10 text-business',
}

const CONFIDENCE_BORDER: Record<ConfidenceLevel, string> = {
  hypothesis: 'border-l-2 border-dashed border-warm-400',
  signal: 'border-l-2 border-solid border-business',
  pattern: 'border-l-2 border-solid border-solution',
  validated: 'border-l-2 border-solid border-market',
  contested: 'border-l-2 border-solid border-danger',
  decided: 'border-l-2 border-solid border-warm-500',
}

export function SpaceNode({
  node, depth, space, isSelected, isHighlighted, isDimmed, isDropTarget,
  expandedIds, onSelect, onToggleExpand,
  canDrag, isValidDropTarget, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
}: SpaceNodeProps) {
  const hasChildren = node.children.length > 0
  const isExpanded = expandedIds.has(node.id)
  const indent = depth * 20 + 12
  const draggable = canDrag(node.type)
  const isValidTarget = isValidDropTarget(node.type, space)

  return (
    <div className={`transition-all duration-200 ${isHighlighted ? 'bg-core/[0.06] ring-1 ring-inset ring-core/20 rounded-sm' : ''}`}>
      <div
        className={`flex items-center gap-1.5 min-h-[32px] cursor-pointer transition-colors hover:bg-warm-50 ${isSelected ? 'bg-core/[0.10] ring-1 ring-inset ring-core/30 rounded-sm' : ''} ${CONFIDENCE_BORDER[node.confidenceLevel]} ${isDropTarget ? 'outline-2 outline-dashed outline-core outline-offset-[-2px] bg-core/[0.06]' : ''}`}
        style={{ paddingLeft: indent, paddingRight: 12 }}
        onClick={() => onSelect(node.id, space)}
        draggable={draggable}
        onDragStart={draggable ? (e) => onDragStart({ type: node.type as any, id: node.id, title: node.title, space }, e) : undefined}
        onDragEnd={onDragEnd}
        onDragOver={isValidTarget ? (e) => onDragOver(node.id, node.type, space, e) : undefined}
        onDragLeave={isValidTarget ? onDragLeave : undefined}
        onDrop={isValidTarget ? (e) => onDrop(node.type, node.id, e) : undefined}
      >
        {/* Expand toggle */}
        {hasChildren ? (
          <button
            className={`w-4 h-4 flex items-center justify-center flex-shrink-0 text-warm-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            onClick={(e) => { e.stopPropagation(); onToggleExpand(node.id) }}
          >
            <ChevronRight size={12} />
          </button>
        ) : (
          <span className="w-4 h-4 flex-shrink-0" />
        )}

        {/* Badge */}
        <span className={`font-display text-[10px] font-semibold uppercase tracking-wide px-1.5 py-px rounded-sm flex-shrink-0 whitespace-nowrap ${BADGE_CLASSES[node.type] || 'bg-warm-200 text-warm-600'}`}>
          {BADGE_LABELS[node.type] || node.type}
        </span>

        {/* Title */}
        <span className={`text-[13px] text-text flex-1 min-w-0 truncate ${node.type === 'persona' ? 'font-semibold' : ''}`}>
          {node.title}
        </span>

        {/* Cross-link count */}
        {node.crossLinkCount > 0 && (
          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-core bg-core/10 px-1.5 py-px rounded-full flex-shrink-0">
            <Link size={10} />
            {node.crossLinkCount}
          </span>
        )}

        {/* Confidence bar */}
        <div className="w-10 h-1 bg-warm-200 rounded-full flex-shrink-0 overflow-hidden">
          <div
            className={`h-full rounded-full ${confidenceFillColor(node.confidenceLevel)}`}
            style={{ width: `${node.confidenceScore}%` }}
          />
        </div>

        {/* Confidence % */}
        <span className="text-[10px] text-text-muted w-7 text-right flex-shrink-0">
          {node.confidenceScore}%
        </span>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && node.children.map(child => (
        <SpaceNode
          key={child.id}
          node={child}
          depth={depth + 1}
          space={space}
          isSelected={false}
          isHighlighted={isHighlighted}
          isDimmed={isDimmed}
          isDropTarget={false}
          expandedIds={expandedIds}
          onSelect={onSelect}
          onToggleExpand={onToggleExpand}
          canDrag={canDrag}
          isValidDropTarget={isValidDropTarget}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onDragEnd={onDragEnd}
        />
      ))}
    </div>
  )
}

function confidenceFillColor(level: ConfidenceLevel): string {
  switch (level) {
    case 'hypothesis': return 'bg-warm-400'
    case 'signal': return 'bg-business'
    case 'pattern': return 'bg-solution'
    case 'validated': return 'bg-market'
    case 'contested': return 'bg-danger'
    case 'decided': return 'bg-warm-500'
  }
}
